import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthStore, useStudioStore } from '@/store'
import { useTelegram } from '@/hooks/useTelegram'
import { useTeamManagement } from '@/hooks/useTeamManagement'
import { supabase } from '@/lib/supabase'
import { softRefresh, hardReset } from '@/lib/appRefresh'
import { sendFeedbackToDeveloper } from '@/lib/feedback'
import { callAuth } from '@/lib/authClient'
import type { Invite } from '@/types'
import { Card, Badge } from '@/components/ui'
import { Button } from '@/components/ui/Button'
import { LoadingScreen } from '@/components/LoadingScreen'
import { StudioPickerSheet } from '@/components/StudioPickerSheet'
import { TelegramLinkCard } from '@/components/TelegramLinkCard'
import { PartnerTeacherAccessPanel } from '@/components/management/PartnerTeacherAccessPanel'
import { isTeacherAppEnabledForPartner } from '@/lib/teacherAppFeature'
import {
  listPartnerTeacherAppStates,
  listPartnerTeacherAssignmentCityIds,
  type PartnerTeacherAppState,
} from '@/services/teacherAppAccessService'
import { UserPlus, Users, Settings, Crown, Building2, BarChart3, Copy, Trash2, Clock, Link2, RotateCcw, MessageSquare, Mail, CheckCircle2, Clock as ClockIcon, Send, User, Pencil, X, Check, LogOut, Info, Phone } from 'lucide-react'

function getCityName(studio: any, cityId: number): string {
  const cityName = studio.city_name
  if (typeof cityName === 'string' && cityName.trim()) return cityName.trim()
  if (cityName && typeof cityName === 'object') {
    const nestedName = cityName.city_name || cityName.name
    if (typeof nestedName === 'string' && nestedName.trim()) return nestedName.trim()
  }
  if (typeof studio.city === 'string' && studio.city.trim()) return studio.city.trim()
  if (studio.city && typeof studio.city === 'object') {
    const nestedName = studio.city.city_name || studio.city.name
    if (typeof nestedName === 'string' && nestedName.trim()) return nestedName.trim()
  }
  return `Город ${cityId}`
}

// Supabase configuration для прямых API запросов
export function GlobalManagementPage() {
  const navigate = useNavigate()
  const { partnerNumber: partnerNumberParam } = useParams<{ partnerNumber: string }>()
  const currentPartnerNumber = partnerNumberParam ? Number(partnerNumberParam) : null
  const { user } = useAuthStore()
  const { studios } = useStudioStore()
  const { showAlert, hapticFeedback } = useTelegram()
  const {
    loadMembers,
    loadInvites,
    createInvite,
    revokeInvite,
    removeMember: removeTeamMember,
    loadMemberStudios,
    updateMemberStudios,
    copyInviteLink,
    confirmMemberRemoval
  } = useTeamManagement()
  const [loading, setLoading] = useState(true)
  const [ownedStudios, setOwnedStudios] = useState<any[]>([])
  const [totalMembers, setTotalMembers] = useState(0)
  const [pendingInvites, setPendingInvites] = useState(0)
  const [invitesList, setInvitesList] = useState<any[]>([])
  const [membersList, setMembersList] = useState<any[]>([])
  const [partnerTeacherStates, setPartnerTeacherStates] = useState<PartnerTeacherAppState[]>([])
  const [creatingInvite, setCreatingInvite] = useState(false)
  const [removingMember, setRemovingMember] = useState<string | number | null>(null)
  const openTeacherAccess = new URLSearchParams(window.location.search).get('section') === 'teacher-access'
  const [selectedRole, setSelectedRole] = useState<'member' | 'manager' | 'teacher'>(openTeacherAccess ? 'teacher' : 'manager')
  // Invite form
  const [selectedInviteStudios, setSelectedInviteStudios] = useState<number[]>([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteFirstName, setInviteFirstName] = useState('')
  const [inviteLastName, setInviteLastName] = useState('')
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null)
  const [inviteStudioSheetOpen, setInviteStudioSheetOpen] = useState(false)
  // Member access editing
  const [memberAccessSheetOpen, setMemberAccessSheetOpen] = useState(false)
  const [memberAccessTarget, setMemberAccessTarget] = useState<{
    kind: 'member'
    userId: string | null
    role: string
    name: string
  } | null>(null)
  const [memberAccessRoleDraft, setMemberAccessRoleDraft] = useState<'member' | 'manager' | 'teacher'>('member')
  const [memberStudiosDraft, setMemberStudiosDraft] = useState<number[]>([])
  const [memberTeacherCityIds, setMemberTeacherCityIds] = useState<number[]>([])
  const [loadingMemberStudios, setLoadingMemberStudios] = useState<string | null>(null)
  const [savingMemberStudios, setSavingMemberStudios] = useState(false)
  const [isRefreshingApp, setIsRefreshingApp] = useState(false)
  const teacherAppEnabled = isTeacherAppEnabledForPartner(currentPartnerNumber)

  useEffect(() => {
    if (!openTeacherAccess || loading) return
    const frame = window.requestAnimationFrame(() => {
      document.getElementById('teacher-access-management')?.scrollIntoView({ block: 'start' })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [loading, openTeacherAccess])

  // Email management
  const [profileEmail, setProfileEmail] = useState(user?.email || '')
  const [emailSaving, setEmailSaving] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [emailVerificationSent, setEmailVerificationSent] = useState(false)
  const [emailResending, setEmailResending] = useState(false)
  const [isEditingEmail, setIsEditingEmail] = useState(false)
  const { login } = useAuthStore()

  // Profile name management (own name)
  const [isEditingName, setIsEditingName] = useState(false)
  const [firstName, setFirstName] = useState(user?.first_name || '')
  const [lastName, setLastName] = useState(user?.last_name || '')
  const [nameSaving, setNameSaving] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)

  // Member name editing by owner
  const [editingMemberNameId, setEditingMemberNameId] = useState<string | null>(null)
  const [memberFirstName, setMemberFirstName] = useState('')
  const [memberLastName, setMemberLastName] = useState('')
  const [savingMemberName, setSavingMemberName] = useState(false)
  const [memberNameError, setMemberNameError] = useState<string | null>(null)

  const normalizedProfileEmail = profileEmail.trim().toLowerCase()
  const isProfileEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedProfileEmail)
  const normalizedInviteEmail = inviteEmail.trim().toLowerCase()
  const isInviteEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedInviteEmail)
  const hasInviteTargetIdentity = Boolean(
    inviteFirstName.trim() && inviteLastName.trim() && isInviteEmailValid
  )
  const partnerOwnedStudios = currentPartnerNumber === null
    ? []
    : ownedStudios.filter((studio) => Number(studio.partner_number) === currentPartnerNumber)
  const isEmailVerified = Boolean(user?.email && user?.email_verified_at)
  const isEmailPendingState = Boolean(user?.email && !user?.email_verified_at)
  const isPendingView = isEmailPendingState && !isEditingEmail

  useEffect(() => {
    if (user?.email) setProfileEmail(user.email)
  }, [user?.email])

  useEffect(() => {
    setFirstName(user?.first_name || '')
    setLastName(user?.last_name || '')
  }, [user?.first_name, user?.last_name])

  const handleSaveName = async () => {
    const canEditOwnName = studios.some(studio => studio.role === 'owner' || studio.access_kind === 'owner')
    if (!canEditOwnName) {
      setNameError('Изменить имя может только владелец')
      setIsEditingName(false)
      return
    }

    setNameSaving(true)
    setNameError(null)
    try {
      await (supabase as any).rpc('update_user_profile', {
        p_first_name: firstName.trim(),
        p_last_name: lastName.trim()
      })
      if (user) {
        login({
          ...user,
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim() || undefined,
          updated_at: new Date().toISOString()
        })
      }
      setIsEditingName(false)
    } catch (err) {
      setNameError(err instanceof Error ? err.message : 'Ошибка сохранения')
    } finally {
      setNameSaving(false)
    }
  }

  const openMemberNameEdit = (member: any) => {
    setEditingMemberNameId(member.user_id)
    setMemberFirstName(member.first_name || '')
    setMemberLastName(member.last_name || '')
    setMemberNameError(null)
  }

  const handleSaveMemberName = async (userId: string) => {
    if (!memberFirstName.trim() || !memberLastName.trim()) {
      setMemberNameError('Укажите имя и фамилию')
      return
    }
    setSavingMemberName(true)
    setMemberNameError(null)
    try {
      await (supabase as any).rpc('update_member_display_name', {
        p_user_id: userId,
        p_first_name: memberFirstName.trim(),
        p_last_name: memberLastName.trim()
      })
      setMembersList(prev => prev.map(m =>
        m.user_id === userId
          ? { ...m, first_name: memberFirstName.trim(), last_name: memberLastName.trim() }
          : m
      ))
      setEditingMemberNameId(null)
    } catch (err) {
      setMemberNameError(err instanceof Error ? err.message : 'Ошибка сохранения')
    } finally {
      setSavingMemberName(false)
    }
  }

  const handleSaveEmail = async () => {
    if (!normalizedProfileEmail || !isProfileEmailValid) { setEmailError('Проверьте формат email'); return }
    setEmailSaving(true)
    setEmailError(null)
    try {
      await callAuth('link-email', { email: normalizedProfileEmail })
      if (user) {
        login({ ...user, email: normalizedProfileEmail, email_verified_at: null, auth_method: user.auth_method, updated_at: new Date().toISOString() })
      }
      setEmailVerificationSent(true)
      setIsEditingEmail(false)
    } catch (err) {
      setEmailVerificationSent(false)
      setEmailError(err instanceof Error ? err.message : 'Ошибка')
    } finally {
      setEmailSaving(false)
      setEmailResending(false)
    }
  }

  const handleAppRefresh = async (confirmMessage: string) => {
    if (isRefreshingApp) return
    if (!confirm(confirmMessage)) return

    setIsRefreshingApp(true)

    try {
      await softRefresh()
    } catch (error) {
      console.error('❌ GlobalManagementPage: failed to refresh app:', error)
      setIsRefreshingApp(false)
    }
  }

  const handleLogout = async () => {
    if (isRefreshingApp) return
    if (!confirm('Выйти из аккаунта? Приложение очистит токен и кэш, после чего потребуется повторный вход.')) return

    setIsRefreshingApp(true)

    try {
      await hardReset()
    } catch (error) {
      console.error('❌ GlobalManagementPage: failed to logout:', error)
      setIsRefreshingApp(false)
    }
  }

  // Функция для отображения роли
  const getRoleBadge = (role: string) => {
    const roleLabels = {
      owner: 'Владелец',
      manager: 'Управляющий',
      member: 'Сотрудник',
      teacher: 'Педагог',
      senior_teacher: 'Старший педагог'
    }
    
    const roleTypes = {
      owner: 'success' as const,
      manager: 'warning' as const,
      member: 'neutral' as const,
      teacher: 'neutral' as const,
      senior_teacher: 'warning' as const
    }

    return (
      <Badge 
        text={roleLabels[role as keyof typeof roleLabels] || role}
        variant={roleTypes[role as keyof typeof roleTypes] || 'neutral'}
      />
    )
  }

  // Диагностика при инициализации
  console.log('🚀 GlobalManagementPage initialized')
  console.log('📱 Device info:', {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    webkitPersistentStorage: !!(navigator as any).webkitPersistentStorage,
    webkitTemporaryStorage: !!(navigator as any).webkitTemporaryStorage
  })
  
  // Проверяем доступность storage APIs
  try {
    localStorage.setItem('test', 'test')
    localStorage.removeItem('test')
    console.log('✅ localStorage available')
  } catch (e) {
    console.error('❌ localStorage not available:', e)
  }
  
  try {
    sessionStorage.setItem('test', 'test')
    sessionStorage.removeItem('test')
    console.log('✅ sessionStorage available')
  } catch (e) {
    console.error('❌ sessionStorage not available:', e)
  }
  
  // Проверяем доступность crypto API
  console.log('🔐 Crypto API:', {
    cryptoAvailable: typeof crypto !== 'undefined',
    randomUUIDAvailable: typeof crypto?.randomUUID === 'function',
    subtleAvailable: typeof crypto?.subtle !== 'undefined'
  })
  
  console.log('👤 Auth state:', {
    user: user?.id,
    userId: user?.id
  })
  console.log('🏢 Studios state:', {
    total: studios.length,
    owned: studios.filter(s => s.role === 'owner').length
  })
  console.log('📊 Detailed studios data:', studios.map(s => ({
    id: s.studio_internal_id,
    number: s.studio_number,
    role: s.role,
    access_kind: s.access_kind
  })))

  const canEditOwnName = studios.some(studio => studio.role === 'owner' || studio.access_kind === 'owner')
  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(' ')

  const profileNameSection = (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <User className="w-5 h-5 text-primary" />
        <h3 className="font-semibold text-base">Профиль</h3>
      </div>

      {isEditingName && canEditOwnName ? (
        <div className="space-y-2">
          <input
            type="text"
            value={firstName}
            onChange={e => { setFirstName(e.target.value); setNameError(null) }}
            placeholder="Имя"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            disabled={nameSaving}
          />
          <input
            type="text"
            value={lastName}
            onChange={e => { setLastName(e.target.value); setNameError(null) }}
            placeholder="Фамилия"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            disabled={nameSaving}
          />
          {nameError && <p className="text-sm text-red-600">{nameError}</p>}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void handleSaveName()} disabled={nameSaving}>
              {nameSaving ? 'Сохраняем...' : 'Сохранить'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setIsEditingName(false)
                setFirstName(user?.first_name || '')
                setLastName(user?.last_name || '')
                setNameError(null)
              }}
              disabled={nameSaving}
            >
              Отмена
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            {displayName ? (
              <p className="text-sm font-medium">{displayName}</p>
            ) : (
              <p className="text-sm text-muted-foreground">Имя не указано</p>
            )}
            {user?.tg_username && (
              <p className="text-xs text-muted-foreground">Telegram: @{user.tg_username}</p>
            )}
            {user?.email && (
              <p className="text-xs text-muted-foreground">Email: {user.email}</p>
            )}
          </div>
          {canEditOwnName && (
            <Button variant="outline" size="sm" onClick={() => setIsEditingName(true)}>
              Изменить имя
            </Button>
          )}
        </div>
      )}
    </Card>
  )

  const emailSection = (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-5 h-5 text-primary" />
        <h3 className="font-semibold text-base">Email для входа</h3>
      </div>
      {isEmailVerified ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 mb-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span className="text-emerald-700 text-sm">Подтвержден: <span className="font-medium">{user?.email}</span></span>
        </div>
      ) : isEmailPendingState ? (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-3">
          <ClockIcon className="w-4 h-4 text-amber-600" />
          <span className="text-amber-700 text-sm">Не подтвержден: <span className="font-medium">{user?.email}</span></span>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground mb-3">Привяжите email для резервного входа без Telegram.</p>
      )}
      {emailVerificationSent && (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 mb-3">
          <Send className="w-4 h-4 text-emerald-600 mt-0.5" />
          <span className="text-emerald-700 text-sm">Ссылка отправлена на <span className="font-medium">{normalizedProfileEmail || user?.email}</span>. Проверьте почту.</span>
        </div>
      )}
      {emailError && <p className="text-sm text-red-600 mb-3">{emailError}</p>}
      {isPendingView && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Button size="sm" className="flex-1" onClick={() => void handleSaveEmail()} disabled={emailSaving || emailResending}>
              {emailResending ? 'Отправляем...' : 'Отправить ссылку снова'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                setIsEditingEmail(true)
                setProfileEmail(user?.email || '')
                setEmailVerificationSent(false)
                setEmailError(null)
              }}
              disabled={emailSaving || emailResending}
            >
              Изменить email
            </Button>
          </div>
        </div>
      )}
      {(!isEmailVerified || isEditingEmail) && !isPendingView && (
        <div className="space-y-2">
          <input
            type="email" value={profileEmail}
            onChange={(e) => { setProfileEmail(e.target.value); setEmailError(null); setEmailVerificationSent(false) }}
            placeholder="you@example.com"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            disabled={emailSaving || emailResending}
          />
          {profileEmail.length > 0 && !isProfileEmailValid && <p className="text-xs text-red-600">Проверьте формат email</p>}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => void handleSaveEmail()}
              disabled={emailSaving || !isProfileEmailValid || normalizedProfileEmail === (user?.email || '').trim().toLowerCase()}
            >
              {emailSaving ? 'Сохраняем...' : 'Сохранить и отправить ссылку'}
            </Button>
            {isEditingEmail && (
              <Button 
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsEditingEmail(false)
                  setProfileEmail(user?.email || '')
                  setEmailVerificationSent(false)
                  setEmailError(null)
                }}
                disabled={emailSaving || emailResending}
              >
                Отмена
              </Button>
            )}
          </div>
        </div>
      )}
      {isEmailVerified && !isEditingEmail && (
        <Button variant="outline" size="sm" onClick={() => {
          setIsEditingEmail(true)
          setProfileEmail(user?.email || '')
          setEmailVerificationSent(false)
          setEmailError(null)
        }}>Изменить email</Button>
      )}
    </Card>
  )

  const telegramSection = <TelegramLinkCard />

  useEffect(() => {
    console.log('🔄 useEffect triggered by studios change')
    console.log('📊 Studios updated:', studios.length)
    console.log('🔗 Supabase auth state:', supabase.auth.getSession())
    
    // Проверяем состояние Supabase клиента
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      console.log('🔐 Current session:', {
        session: session ? 'active' : 'none',
        userId: session?.user?.id,
        error: error?.message
      })
    })
    
    loadManagementData()
  }, [studios, currentPartnerNumber, user?.id])

  const loadManagementData = async () => {
    console.log('🔄 Loading management data - START')
    console.log('📊 Current user:', user?.id)
    
    if (!user) {
      console.log('❌ No user, skipping load')
      return
    }

    try {
      setLoading(true)

      // Получаем студии, которыми владеет пользователь
      const owned = [...studios]
        .filter(studio => studio.role === 'owner')
        .sort((a, b) => {
          const cityA = (a.city?.city_name || '').trim()
          const cityB = (b.city?.city_name || '').trim()
          const cityCompare = cityA.localeCompare(cityB, 'ru', { sensitivity: 'base' })
          if (cityCompare !== 0) return cityCompare
          return String(a.studio_number || '').localeCompare(String(b.studio_number || ''), 'ru', { numeric: true })
        })
      console.log('🏢 Owned studios:', owned.length, owned.map(s => ({ id: s.studio_internal_id, number: s.studio_number })))
      setOwnedStudios(owned)
      // По дефолту выбираем все студии; убираем те, что вышли из владения
      setSelectedInviteStudios(prev => {
        const allIds = owned.map(s => s.studio_internal_id)
        const filtered = prev.filter(id => allIds.includes(id))
        return filtered.length > 0 ? filtered : allIds
      })

      if (owned.length > 0) {
        const studioIds = owned.map(s => s.studio_internal_id)
        console.log('🔢 Studio IDs for queries:', studioIds)

        console.log('👥 Loading team members via useTeamManagement...')
        const members = await loadMembers({ id: user.id })
        setMembersList(members)
        setTotalMembers(members.length)

        console.log('💌 Loading invites via useTeamManagement...')
        const invites = await loadInvites({ id: user.id })
        setInvitesList(invites)
        setPendingInvites(invites.length)

        // This is also the source for the senior-teacher badge in the team.
        // A regular teacher role and an approved senior access are separate
        // concepts, so the generic team RPC cannot provide the badge alone.
        const teacherStates = currentPartnerNumber === null
          ? []
          : await listPartnerTeacherAppStates(currentPartnerNumber).catch(() => [])
        setPartnerTeacherStates(teacherStates)

      } else {
        console.log('ℹ️ No owned studios, clearing invites and members')
        setInvitesList([])
        setPendingInvites(0)
        setMembersList([])
        setTotalMembers(0)
        setPartnerTeacherStates([])
      }
      
      console.log('✅ Management data loaded successfully')
    } catch (err) {
      console.error('❌ Error in loadManagementData:', {
        error: err,
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined
      })
    } finally {
      setLoading(false)
      console.log('🏁 Load management data finished')
    }
  }

  const createGlobalInvite = async () => {
    console.log('🎯 Creating global invite - START')
    console.log('📊 Current state:', {
      user: user?.id,
      ownedStudios: ownedStudios.length,
      selectedRole,
      selectedInviteStudios,
      userAgent: navigator.userAgent
    })

    if (!user || ownedStudios.length === 0) {
      console.log('❌ Early return - no user or studios')
      return
    }

    if (selectedInviteStudios.length === 0) {
      hapticFeedback.notification('error')
      showAlert('Выберите хотя бы одну студию для приглашения')
      return
    }

    if (!hasInviteTargetIdentity) {
      hapticFeedback.notification('error')
      showAlert('Укажите имя, фамилию и корректный email сотрудника')
      return
    }

    try {
      setCreatingInvite(true)
      hapticFeedback.impact('light')

      // Если выбраны все студии — сохраняем как 'all' (доступ ко всем, включая будущие)
      const isAllStudios = selectedInviteStudios.length === ownedStudios.length
      const insertedInvite = await createInvite(
        { id: user.id },
        {
          role: selectedRole === 'manager' ? 'manager' : 'member',
          studioIds: selectedInviteStudios,
          accessScope: isAllStudios ? 'all' : 'specific',
          targetEmail: normalizedInviteEmail,
          targetFirstName: inviteFirstName,
          targetLastName: inviteLastName
        }
      )

      console.log('✅ Invite created successfully:', insertedInvite)

      let successMessage = `Приглашение создано и отправлено на ${normalizedInviteEmail}.`
      try {
        await callAuth('email-request', {
          email: normalizedInviteEmail,
          invite_token: insertedInvite.token
        })
      } catch (emailError) {
        console.error('Инвайт создан, но письмо не отправлено:', emailError)
        successMessage = `Приглашение создано, но письмо не удалось отправить на ${normalizedInviteEmail}. Повторите отправку в списке активных приглашений.`
        try {
          await copyInviteLink(insertedInvite.token)
          successMessage += ' Ссылка скопирована в буфер обмена.'
        } catch (clipboardError) {
          console.warn('📋 Clipboard write failed:', clipboardError)
        }
      }

      // Обновляем локальное состояние сразу же, добавляя новое приглашение
      console.log('🔄 Updating local state...')
      console.log('📊 Current invites count before:', invitesList.length)
      
      setInvitesList(prev => {
        const newList = [insertedInvite, ...prev]
        console.log('📊 New invites count after:', newList.length)
        return newList
      })
      setPendingInvites(prev => {
        const newCount = prev + 1
        console.log('📊 New pending count:', newCount)
        return newCount
      })
      
      setInviteEmail('')
      setInviteFirstName('')
      setInviteLastName('')
      hapticFeedback.notification('success')
      showAlert(successMessage)
      console.log('✅ Create invite process completed successfully')
      
    } catch (err) {
      console.error('❌ Error in createGlobalInvite:', {
        error: err,
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined
      })
      hapticFeedback.notification('error')
      showAlert(`Ошибка создания приглашения: ${err instanceof Error ? err.message : 'Неизвестная ошибка'}`)
    } finally {
      setCreatingInvite(false)
      console.log('🏁 Create invite process finished')
    }
  }

  const resendStaffInvite = async (invite: Invite) => {
    const targetEmail = typeof invite.target_email === 'string'
      ? invite.target_email.trim().toLowerCase()
      : ''
    if (!targetEmail || resendingInviteId !== null) return

    try {
      setResendingInviteId(invite.id)
      await callAuth('email-request', {
        email: targetEmail,
        invite_token: invite.token
      })
      hapticFeedback.notification('success')
      showAlert(`Ссылка повторно отправлена на ${targetEmail}.`)
    } catch (emailError) {
      console.error('Ошибка повторной отправки приглашения:', emailError)
      hapticFeedback.notification('error')
      showAlert('Не удалось отправить письмо. Попробуйте ещё раз или скопируйте ссылку.')
    } finally {
      setResendingInviteId(null)
    }
  }

  const deleteInvite = async (inviteId: string) => {
    try {
      console.log('🗑️ Deleting invite:', inviteId)
      console.log('🔍 InviteId type:', typeof inviteId)
      console.log('🔍 Current invites list:', invitesList.map(inv => ({ id: inv.id, token: inv.token })))
      
      if (!inviteId || inviteId === 'undefined') {
        throw new Error('Invalid invite ID provided')
      }
      
      // Используем apiRequest с автообновлением токена
      await revokeInvite({ id: user!.id }, inviteId)

      console.log('✅ Invite deleted successfully')
      
      // Обновляем локальное состояние сразу же
      setInvitesList(prev => prev.filter(invite => invite.id !== inviteId))
      setPendingInvites(prev => prev - 1)

      hapticFeedback.notification('success')
      showAlert('Приглашение удалено')
      
      // Перезагружаем данные для синхронизации
      setTimeout(() => {
        loadManagementData()
      }, 500)
      
    } catch (err) {
      console.error('❌ Error in deleteInvite:', err)
      hapticFeedback.notification('error')
      showAlert(`Ошибка при удалении приглашения: ${err instanceof Error ? err.message : 'Неизвестная ошибка'}`)
      
      // При ошибке перезагружаем данные для синхронизации
      setTimeout(() => {
        loadManagementData()
      }, 500)
    }
  }

  const openMemberAccessSheet = async (member: any) => {
    if (!user) return
    const userId = String(member.user_id)
    const name = [member.first_name, member.last_name].filter(Boolean).join(' ') || 'Сотрудник'
    try {
      setLoadingMemberStudios(userId)
      let studioIds: number[]
      if (member.access_scope === 'all') {
        studioIds = ownedStudios.map(s => s.studio_internal_id)
      } else {
        const rows = await loadMemberStudios(userId)
        studioIds = rows.map(r => r.studio_internal_id)
      }
      setMemberStudiosDraft(studioIds)
      setMemberAccessRoleDraft(member.role === 'manager' ? 'manager' : member.role === 'teacher' ? 'teacher' : 'member')
      const teacherState = partnerTeacherStates.find(state => state.user_id === userId)
      if (teacherState?.teacher_id && currentPartnerNumber !== null) {
        const cityIds = await listPartnerTeacherAssignmentCityIds(teacherState.teacher_id, currentPartnerNumber)
        setMemberTeacherCityIds(cityIds)
      } else {
        setMemberTeacherCityIds([])
      }
      setMemberAccessTarget({
        kind: 'member',
        userId,
        role: member.role,
        name
      })
      setMemberAccessSheetOpen(true)
    } catch (err) {
      console.error('Failed to load member studios:', err)
      hapticFeedback.notification('error')
      showAlert(`Ошибка загрузки доступов: ${err instanceof Error ? err.message : 'Неизвестная ошибка'}`)
    } finally {
      setLoadingMemberStudios(null)
    }
  }

  const saveMemberAccess = async () => {
    if (!user || !memberAccessTarget) return

    try {
      setSavingMemberStudios(true)

      const role = memberAccessRoleDraft
      if (!memberAccessTarget.userId) throw new Error('Пользователь не найден')
      const isAllStudios = memberStudiosDraft.length === ownedStudios.length
      const scopeToSave: 'all' | 'specific' = isAllStudios ? 'all' : 'specific'
      await updateMemberStudios(
        memberAccessTarget.userId,
        memberStudiosDraft,
        role,
        scopeToSave
      )
      const newCount = isAllStudios ? -1 : memberStudiosDraft.length
      setMembersList(prev => prev.map(m =>
        String(m.user_id) === memberAccessTarget.userId
          ? { ...m, role, access_scope: scopeToSave, assigned_studios_count: newCount }
          : m
      ))
      setMemberAccessSheetOpen(false)
      setMemberAccessTarget(null)
      hapticFeedback.notification('success')
      showAlert('Доступы сотрудника обновлены')
    } catch (err) {
      console.error('Failed to save member access:', err)
      hapticFeedback.notification('error')
      showAlert(`Ошибка сохранения: ${err instanceof Error ? err.message : 'Неизвестная ошибка'}`)
    } finally {
      setSavingMemberStudios(false)
    }
  }

  const canEditTeacherRole = Boolean(
    teacherAppEnabled
    && memberAccessTarget
    && (
      memberAccessTarget.role === 'teacher'
      || partnerTeacherStates.some(state => state.user_id === memberAccessTarget.userId)
    )
  )

  const removeMember = async (memberUserId: string, memberName: string) => {
    if (!user) return

    const confirmed = await confirmMemberRemoval(memberName)
    if (!confirmed) {
      return
    }

    try {
      setRemovingMember(memberUserId)
      console.log('🗑️ Removing member:', memberUserId, 'from team_members')
      
      // Удаляем сотрудника из team_members (remove_team_member_v2)
      await removeTeamMember(
        { id: user.id },
        memberUserId
      )

      console.log('✅ Member removed from team_members')

      // Обновляем локальное состояние сразу же
      setMembersList(prev => prev.filter(member => String(member.user_id) !== memberUserId))
      setTotalMembers(prev => prev - 1)

      hapticFeedback.notification('success')
      showAlert(`Сотрудник ${memberName} удален из команды`)
      console.log('✅ Member removed successfully')
      
    } catch (err) {
      console.error('❌ Error in removeMember:', err)
      hapticFeedback.notification('error')
      showAlert(`Ошибка при удалении сотрудника: ${err instanceof Error ? err.message : 'Неизвестная ошибка'}`)
    } finally {
      setRemovingMember(null)
    }
  }

  const getMemberEmail = (member: any) => {
    const directEmail = typeof member.email === 'string' ? member.email.trim() : ''
    const authEmail = typeof member.auth_email === 'string' ? member.auth_email.trim() : ''
    return directEmail || authEmail
  }

  const getMemberFullName = (member: any) => {
    return [member.first_name, member.last_name]
      .map(value => typeof value === 'string' ? value.trim() : '')
      .filter(Boolean)
      .join(' ')
  }

  const getMemberUsername = (member: any) => {
    return typeof member.tg_username === 'string' ? member.tg_username.trim() : ''
  }

  const getMemberDisplayName = (member: any) => {
    const fullName = getMemberFullName(member)
    const username = getMemberUsername(member)
    const email = getMemberEmail(member)
    return fullName || (username ? `@${username}` : '') || email || 'Без имени'
  }

  if (loading) {
    return <LoadingScreen />
  }

  if (ownedStudios.length === 0) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">⚙️ Управление</h1>
            <p className="text-muted-foreground">
              Управление пользователями и приглашениями
            </p>
          </div>

          {/* Кнопки разработчика - доступны всем */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div 
              onClick={() => {
                try {
                  hapticFeedback.impact('light')
                  sendFeedbackToDeveloper()
                } catch (error) {
                  console.error('Failed to send feedback:', error)
                  showAlert('Ошибка отправки обратной связи')
                }
              }}
              className="cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <Card className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Обратная связь</p>
                    <p className="text-xs text-muted-foreground">
                      Сообщите о проблеме или предложите улучшение
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            <div 
              onClick={() => void handleAppRefresh('Обновить приложение? Это перезагрузит страницу.')}
              className={`transition-all ${isRefreshingApp ? 'cursor-wait opacity-80' : 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]'}`}
            >
              <Card className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <RotateCcw className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Обновить приложение</p>
                    <p className="text-xs text-muted-foreground">
                      {isRefreshingApp ? 'Обновляем...' : 'Очистка кэша и перезагрузка приложения'}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            <div
              onClick={() => void handleLogout()}
              className={`transition-all ${isRefreshingApp ? 'cursor-wait opacity-80' : 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]'}`}
            >
              <Card className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <LogOut className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Выйти из аккаунта</p>
                    <p className="text-xs text-muted-foreground">
                      {isRefreshingApp ? 'Выходим...' : 'Завершение сеанса и повторный вход'}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {profileNameSection}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {emailSection}
            {telegramSection}
          </div>

          <Card className="p-8 text-center">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Crown className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-medium mb-3">
              Нет студий для управления
            </h3>
            <p className="text-muted-foreground">
              У вас нет студий, которыми вы владеете. Функции управления доступны только владельцам студий.
            </p>
          </Card>

          {/* Информация о версии */}
          <div className="text-center text-xs text-muted-foreground">
            Версия приложения: {(import.meta.env.VITE_APP_VERSION || '1.0.0').slice(0, 7)}
            {localStorage.getItem('last_cache_clear') && (
              <span className="ml-2">
                • Последняя очистка кэша: {new Date(localStorage.getItem('last_cache_clear')!).toLocaleDateString('ru-RU')}
              </span>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Заголовок */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2">⚙️ Управление</h1>
          <p className="text-muted-foreground">
            Управление пользователями и приглашениями для всех ваших студий
          </p>
        </div>

        {/* Статистика */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ваши студии</p>
                <p className="text-xl font-bold">{ownedStudios.length}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ваша команда</p>
                <p className="text-xl font-bold">{totalMembers}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <UserPlus className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Активные приглашения</p>
                <p className="text-xl font-bold">{pendingInvites}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Кнопки разработчика */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div 
            onClick={() => {
              try {
                hapticFeedback.impact('light')
                sendFeedbackToDeveloper()
              } catch (error) {
                console.error('Failed to send feedback:', error)
                showAlert('Ошибка отправки обратной связи')
              }
            }}
            className="cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <Card className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium mb-1">Обратная связь</p>
                  <p className="text-xs text-muted-foreground">
                    Сообщите о проблеме или предложите улучшение
                  </p>
                </div>
              </div>
            </Card>
          </div>

          <div 
            onClick={() => void handleAppRefresh('Очистить кэш приложения? Это перезагрузит страницу.')}
            className={`transition-all ${isRefreshingApp ? 'cursor-wait opacity-80' : 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]'}`}
          >
            <Card className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <RotateCcw className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium mb-1">Обновить приложение</p>
                  <p className="text-xs text-muted-foreground">
                    {isRefreshingApp ? 'Обновляем...' : 'Очистка кэша и перезагрузка приложения'}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          <div
            onClick={() => void handleLogout()}
            className={`transition-all ${isRefreshingApp ? 'cursor-wait opacity-80' : 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]'}`}
          >
            <Card className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <LogOut className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium mb-1">Выйти из аккаунта</p>
                  <p className="text-xs text-muted-foreground">
                    {isRefreshingApp ? 'Выходим...' : 'Завершение сеанса и повторный вход'}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {profileNameSection}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {emailSection}
          {telegramSection}
        </div>

        {/* Информация о версии */}
        <div className="text-center text-xs text-muted-foreground">
          Версия приложения: {(import.meta.env.VITE_APP_VERSION || '1.0.0').slice(0, 7)}
          {localStorage.getItem('last_cache_clear') && (
            <span className="ml-2">
              • Последняя очистка кэша: {new Date(localStorage.getItem('last_cache_clear')!).toLocaleDateString('ru-RU')}
            </span>
          )}
        </div>

        {/* Единая выдача доступа */}
        <Card id="teacher-access-management" className="scroll-mt-4 p-6">
          <div className="flex items-start gap-4">
            <div className="hidden rounded-lg bg-primary/10 p-3 sm:block">
              <UserPlus className="h-6 w-6 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-4">
                <h3 className="font-medium">Выдать доступ</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Выберите уровень доступа — форма ниже изменится автоматически.
                </p>
              </div>

              <div className="mb-5 inline-flex max-w-full gap-1 overflow-x-auto rounded-xl border border-border bg-muted/40 p-1">
                {([
                  ['member', 'Сотрудник'],
                  ['manager', 'Управляющий'],
                ] as const).map(([role, label]) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setSelectedRole(role)}
                    className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                      selectedRole === role
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
                    }`}
                  >
                    {label}
                  </button>
                  ))}
                <button
                  type="button"
                  onClick={() => setSelectedRole('teacher')}
                  className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    selectedRole === 'teacher'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
                  }`}
                >
                  Педагог
                </button>
              </div>

              {selectedRole === 'teacher' && currentPartnerNumber !== null ? (
                <PartnerTeacherAccessPanel
                  partnerNumber={currentPartnerNumber}
                  initialShowStates={openTeacherAccess}
                  cities={partnerOwnedStudios.map((studio) => ({
                    city_id: Number(studio.city_id),
                    city_name: studio.city_name || studio.city || undefined,
                  }))}
                  onStatesChange={setPartnerTeacherStates}
                />
              ) : (
              <>
                  <div className="mb-4 space-y-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium">Имя</label>
                        <input
                          type="text"
                          value={inviteFirstName}
                          onChange={event => setInviteFirstName(event.target.value)}
                          placeholder="Имя сотрудника"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium">Фамилия</label>
                        <input
                          type="text"
                          value={inviteLastName}
                          onChange={event => setInviteLastName(event.target.value)}
                          placeholder="Фамилия сотрудника"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">Email</label>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={event => setInviteEmail(event.target.value)}
                        placeholder="name@example.com"
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        Одноразовая ссылка применит выбранный доступ после входа.
                      </p>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">Доступ к студиям</label>
                      <button
                        type="button"
                        onClick={() => setInviteStudioSheetOpen(true)}
                        className="flex w-full items-center justify-between rounded-lg border border-border bg-background p-3 text-sm transition-colors hover:bg-muted/50"
                      >
                        <span>
                          {selectedInviteStudios.length === 0
                            ? 'Выбрать студии...'
                            : selectedInviteStudios.length === ownedStudios.length
                              ? 'Все студии'
                              : `Выбрано ${selectedInviteStudios.length} из ${ownedStudios.length}`}
                        </span>
                        <span className="text-xs text-muted-foreground">Изменить →</span>
                      </button>
                    </div>
                  </div>

                  <Button
                    onClick={createGlobalInvite}
                    disabled={creatingInvite || selectedInviteStudios.length === 0 || !hasInviteTargetIdentity}
                    className="w-full sm:w-auto"
                  >
                    {creatingInvite ? 'Создание...' : 'Создать и отправить'}
                  </Button>
              </>
              )}
            </div>
          </div>
        </Card>

        {/* Список активных приглашений */}
        {invitesList.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Link2 className="w-5 h-5" />
              <h3 className="font-medium">Активные приглашения ({invitesList.length})</h3>
            </div>
            <div className="space-y-3">
              {invitesList.map((invite) => (
                <div key={invite.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {getRoleBadge(invite.role)}
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">
                      {invite.access_scope === 'all'
                        ? 'Все студии'
                        : `Студий: ${invite.studio_ids?.length ?? 0}`}
                      {(invite.max_uses ?? 1) > 1 && (
                        <span className="ml-2">· Исп.: {invite.current_uses ?? 0}/{invite.max_uses}</span>
                      )}
                    </p>
                    {invite.target_email && (
                      <p className="text-sm text-muted-foreground mb-1 break-words">
                        {[invite.target_first_name, invite.target_last_name].filter(Boolean).join(' ')} · {invite.target_email}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>
                        Истекает: {new Date(invite.expires_at).toLocaleDateString('ru-RU', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {invite.target_email && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void resendStaffInvite(invite)}
                        disabled={resendingInviteId !== null}
                        title="Отправить приглашение повторно"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          await copyInviteLink(invite.token)
                          hapticFeedback.notification('success')
                          showAlert('Инструкция со ссылками скопирована в буфер обмена')
                        } catch {
                          hapticFeedback.notification('error')
                          showAlert('Ошибка при копировании ссылки')
                        }
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        console.log('🔍 Delete button clicked for invite:', { id: invite.id, token: invite.token, fullInvite: invite })
                        if (!invite.id) {
                          console.error('❌ No invite ID found!')
                          showAlert('Ошибка: не найден ID приглашения')
                          return
                        }
                        deleteInvite(invite.id)
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {membersList.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5" />
              <h3 className="font-medium">Команда ({membersList.length})</h3>
            </div>
            <div className="space-y-3">
              {membersList.map((member) => {
                const displayName = getMemberDisplayName(member)
                const username = getMemberUsername(member)
                const email = getMemberEmail(member)
                const hasTelegram = member.telegram_id !== null && member.telegram_id !== undefined
                const telegramLine = hasTelegram
                  ? (username ? `@${username}` : 'Telegram привязан, ник не указан')
                  : 'Telegram не привязан'

                const isEditingThisMember = editingMemberNameId === member.user_id

                return (
                  <div key={member.user_id} className="p-3 bg-muted/50 rounded-lg">
                    {isEditingThisMember ? (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground mb-1">Редактирование имени</p>
                        <input
                          type="text"
                          value={memberFirstName}
                          onChange={e => { setMemberFirstName(e.target.value); setMemberNameError(null) }}
                          placeholder="Имя *"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                          disabled={savingMemberName}
                        />
                        <input
                          type="text"
                          value={memberLastName}
                          onChange={e => { setMemberLastName(e.target.value); setMemberNameError(null) }}
                          placeholder="Фамилия *"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                          disabled={savingMemberName}
                        />
                        {memberNameError && <p className="text-xs text-red-600">{memberNameError}</p>}
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => void handleSaveMemberName(member.user_id)} disabled={savingMemberName}>
                            <Check className="w-4 h-4 mr-1" />{savingMemberName ? 'Сохраняем...' : 'Сохранить'}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setEditingMemberNameId(null)} disabled={savingMemberName}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className="font-medium truncate" title={displayName}>
                              {displayName}
                            </h4>
                            {getRoleBadge(
                              member.role === 'teacher' && partnerTeacherStates.some(
                                state => state.user_id === String(member.user_id) && state.request_status === 'approved'
                              )
                                ? 'senior_teacher'
                                : member.role
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {!hasTelegram && email && <p>Вход через почту</p>}
                            <p>{telegramLine}</p>
                            {email ? (
                              (hasTelegram || displayName !== email) && (
                                <p className="flex items-center gap-1 min-w-0">
                                  <Mail className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">Почта: {email}</span>
                                </p>
                              )
                            ) : (
                              <p>Почта не привязана</p>
                            )}
                            <p>
                              {member.access_scope === 'all'
                                ? 'Все студии'
                                : `Студий: ${member.assigned_studios_count ?? 0}`}
                            </p>
                            {member.created_at && (
                              <p>С {new Date(member.created_at).toLocaleDateString('ru-RU')}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openMemberNameEdit(member)}
                            className="px-2"
                            title="Изменить имя"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openMemberAccessSheet(member)}
                            disabled={loadingMemberStudios === member.user_id}
                          >
                            {loadingMemberStudios === member.user_id ? 'Загрузка...' : 'Доступ'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeMember(
                              member.user_id,
                              displayName === 'Без имени' ? 'Сотрудник' : displayName
                            )}
                            disabled={removingMember === member.user_id}
                            className="px-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Card>
        )}
        <div className="h-20"></div>
      </div>

      {/* Sheet: выбор студий для инвайта */}
      <StudioPickerSheet
        isOpen={inviteStudioSheetOpen}
        onClose={() => setInviteStudioSheetOpen(false)}
        title="Студии для приглашения"
        studios={ownedStudios}
        selectedIds={selectedInviteStudios}
        onChange={setSelectedInviteStudios}
      />

      {/* Sheet: редактирование уровня и области доступа */}
      <StudioPickerSheet
        isOpen={memberAccessSheetOpen}
        onClose={() => { setMemberAccessSheetOpen(false); setMemberAccessTarget(null) }}
        title={memberAccessTarget ? `Доступ — ${memberAccessTarget.name}` : 'Доступ'}
        studios={ownedStudios}
        selectedIds={memberStudiosDraft}
        onChange={setMemberStudiosDraft}
        showStudioSelection={memberAccessRoleDraft !== 'teacher'}
        headerContent={memberAccessTarget ? (
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium mb-2">Уровень доступа</p>
              <div className={canEditTeacherRole ? 'grid grid-cols-3 gap-2' : 'grid grid-cols-2 gap-2'}>
                {(['member', 'manager', ...(canEditTeacherRole ? ['teacher'] : [])] as Array<'member' | 'manager' | 'teacher'>).map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setMemberAccessRoleDraft(role)}
                    className={`rounded-lg border px-2 py-2 text-center text-xs transition-colors ${memberAccessRoleDraft === role ? 'border-primary bg-primary text-primary-foreground shadow-sm' : 'border-border bg-background hover:bg-muted/40'}`}
                  >
                    <span className="font-medium">
                      {role === 'manager' ? 'Управляющий' : role === 'teacher' ? 'Педагог' : 'Сотрудник'}
                    </span>
                  </button>
                ))}
              </div>
              {memberAccessRoleDraft === 'teacher' && memberAccessTarget.userId && (
                <div className="mt-3 space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    <span className="font-medium text-foreground">Доступные города:</span>{' '}
                    {memberTeacherCityIds.length > 0
                      ? memberTeacherCityIds
                        .map(cityId => getCityName(
                          partnerOwnedStudios.find(studio => Number(studio.city_id) === cityId) || {},
                          cityId
                        ))
                        .filter((city, index, all) => all.indexOf(city) === index)
                        .join(', ')
                      : 'Города работы не определены'}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : null}
        confirmLabel="Сохранить"
        onConfirm={saveMemberAccess}
        saving={savingMemberStudios}
      />

      {/* Footer Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border px-4 py-2">
        <div className="flex justify-around">
          <button
            onClick={() => navigate('/')}
            className="flex flex-col items-center p-2 rounded-lg transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <Building2 className="w-5 h-5 mb-1" />
            <span className="text-xs">Студии</span>
          </button>
          
          <button
            onClick={() => navigate('/analytics')}
            className="flex flex-col items-center p-2 rounded-lg transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <BarChart3 className="w-5 h-5 mb-1" />
            <span className="text-xs">Аналитика</span>
          </button>
          
          <button
            disabled
            className="flex flex-col items-center p-2 rounded-lg transition-colors text-primary bg-primary/10"
          >
            <Settings className="w-5 h-5 mb-1" />
            <span className="text-xs">Управление</span>
          </button>
        </div>
      </div>
    </div>
  )
}
